import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { DashboardLayout } from "../dashboard/components/DashboardLayout";
import { DashboardNav } from "../dashboard/components/DashboardNav";
import { DashboardHeader } from "../dashboard/components/DashboardHeader";
import { ConfirmModal } from "../../components/ConfirmModal";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { LogoutIcon } from "../../assets/LogoutIcon";
import "./MessagingLayout.css";

/**
 * Shell for the Messaging destination.
 *
 * Messaging is its own top-level area, not a CRM page: it has its own drawer entry, its own
 * header, and — unlike the CRM — it is open to every coach, because a coach texting a recruit is
 * the whole feature. Only the Numbers tab is admin-only, since a Twilio number is an org asset
 * with a monthly cost and who may send from it is an access-control decision.
 */
export const MessagingLayout = () => {
  const { user, isSystemAdmin, logout } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const isAdmin = user?.profile?.role === "admin" || isSystemAdmin;

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNav onLogout={() => setShowLogoutModal(true)} />
      <div className="dashboard-content">
        <DashboardHeader title="Messaging" userEmail={user?.email || "Unknown"} />
        <div className="dashboard-section-container">
          <nav className="messaging-tabs" aria-label="Messaging sections">
            <NavLink
              to="/messaging"
              end
              className={({ isActive }) =>
                `messaging-tabs__tab${isActive ? " messaging-tabs__tab--active" : ""}`
              }
            >
              Inbox
            </NavLink>
            <NavLink
              to="/messaging/templates"
              className={({ isActive }) =>
                `messaging-tabs__tab${isActive ? " messaging-tabs__tab--active" : ""}`
              }
            >
              Templates
            </NavLink>
            {isAdmin && (
              <NavLink
                to="/messaging/numbers"
                className={({ isActive }) =>
                  `messaging-tabs__tab${isActive ? " messaging-tabs__tab--active" : ""}`
                }
              >
                Numbers
              </NavLink>
            )}
          </nav>
          <ErrorBoundary
            fallbackTitle="Messaging Error"
            fallbackMessage="This section encountered an error. Try refreshing the page."
          >
            <Outlet />
          </ErrorBoundary>
        </div>
      </div>

      <ConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogoutConfirm}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Yes, Logout"
        cancelText="Cancel"
        icon={<LogoutIcon />}
      />
    </DashboardLayout>
  );
};
